import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { toast } from 'react-hot-toast';
import { useSite } from '../../context/SiteContext';
import { getBusinessDateISO } from '../../utils/businessDate';
import { addNovolexLogo } from '../../utils/pdfBranding';

const formatInteger = (value) => Number(value || 0).toLocaleString('en-US');
export const formatPounds = (value) => Number(value || 0).toLocaleString('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const dateDaysBefore = (date, days) => {
  const [year, month, day] = date.split('-').map(Number);
  const result = new Date(Date.UTC(year, month - 1, day));
  result.setUTCDate(result.getUTCDate() - days);
  return result.toISOString().slice(0, 10);
};

const drawPoundsBarChart = (doc, machines, startY, margin) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const chartWidth = pageWidth - (margin * 2);
  const chartHeight = 210;
  const plotTop = startY + 32;
  const plotBottom = startY + chartHeight - 34;
  const plotHeight = plotBottom - plotTop;
  const labelWidth = 54;
  const plotLeft = margin + labelWidth;
  const plotRight = margin + chartWidth - 8;
  const plotWidth = plotRight - plotLeft;
  const maximum = Math.max(0, ...machines.map((machine) => Number(machine.estimated_lbs || 0)));

  doc.setFillColor(248, 250, 252);
  doc.setDrawColor(203, 213, 225);
  doc.roundedRect(margin, startY, chartWidth, chartHeight, 8, 8, 'FD');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(30, 64, 175);
  doc.text('Libras estimadas por máquina', margin + 16, startY + 22);

  if (machines.length === 0) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.setTextColor(100, 116, 139);
    doc.text('No hay producción en el rango seleccionado.', pageWidth / 2, startY + 132, { align: 'center' });
    return startY + chartHeight;
  }

  for (let tick = 0; tick <= 4; tick += 1) {
    const ratio = tick / 4;
    const y = plotBottom - (plotHeight * ratio);
    doc.setDrawColor(226, 232, 240);
    doc.line(plotLeft, y, plotRight, y);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    doc.text(formatPounds(maximum * ratio), plotLeft - 6, y + 3, { align: 'right' });
  }

  const slotWidth = plotWidth / machines.length;
  const barWidth = Math.max(5, Math.min(34, slotWidth * 0.62));
  machines.forEach((machine, index) => {
    const value = Number(machine.estimated_lbs || 0);
    const barHeight = maximum > 0 ? (value / maximum) * plotHeight : 0;
    const x = plotLeft + (slotWidth * index) + ((slotWidth - barWidth) / 2);
    doc.setFillColor(217, 119, 6);
    if (barHeight > 0) doc.roundedRect(x, plotBottom - barHeight, barWidth, barHeight, 2, 2, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(Math.max(6, Math.min(9, slotWidth * 0.28)));
    doc.setTextColor(51, 65, 85);
    doc.text(String(machine.code || ''), x + (barWidth / 2), plotBottom + 14, { align: 'center' });
    if (slotWidth >= 52 && value > 0) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(146, 64, 14);
      doc.text(formatPounds(value), x + (barWidth / 2), plotBottom - barHeight - 5, { align: 'center' });
    }
  });

  return startY + chartHeight;
};

export const buildPoundsPdf = (report) => {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a3' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 36;
  const totals = report.totals || {};

  doc.setProperties({
    title: `Reporte de libras ${report.start_date} a ${report.end_date}`,
    subject: 'Libras estimadas por máquina usando bolsas buenas y el factor LBS del Item',
  });

  addNovolexLogo(doc, { x: margin, y: 14, width: 82 });
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.setTextColor(30, 64, 175);
  doc.text('Reporte de libras', margin + 100, 42);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.setTextColor(51, 65, 85);
  doc.text(`${report.site?.code || ''}${report.site?.name ? ` - ${report.site.name}` : ''}`, margin, 68);
  doc.text(`Periodo: ${report.start_date} al ${report.end_date}`, margin, 84);
  doc.text(report.include_open ? 'Incluye reportes abiertos y cerrados' : 'Solo reportes cerrados', margin, 100);

  const cards = [
    { label: 'Máquinas con producción', value: formatInteger(totals.machine_count) },
    { label: 'Bolsas buenas', value: formatInteger(totals.bags_good) },
    { label: 'Libras estimadas', value: formatPounds(totals.estimated_lbs) },
  ];
  const cardY = 112;
  const cardGap = 14;
  const cardWidth = (pageWidth - (margin * 2) - (cardGap * 2)) / 3;
  cards.forEach((card, index) => {
    const x = margin + (index * (cardWidth + cardGap));
    doc.setFillColor(239, 246, 255);
    doc.setDrawColor(191, 219, 254);
    doc.roundedRect(x, cardY, cardWidth, 52, 7, 7, 'FD');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(29, 78, 216);
    doc.text(card.label.toUpperCase(), x + 12, cardY + 18);
    doc.setFontSize(18);
    doc.setTextColor(23, 37, 84);
    doc.text(card.value, x + 12, cardY + 39);
  });

  const chartEndY = drawPoundsBarChart(doc, report.machines || [], 178, margin);
  const rows = (report.machines || []).map((machine) => [
    machine.code,
    machine.name || '',
    formatInteger(machine.bags_good),
    formatPounds(machine.estimated_lbs),
    `${Number(machine.percentage || 0).toFixed(2)}%`,
    formatInteger(machine.bags_without_factor),
  ]);
  rows.push([
    'TOTAL',
    '',
    formatInteger(totals.bags_good),
    formatPounds(totals.estimated_lbs),
    Number(totals.estimated_lbs || 0) > 0 ? '100.00%' : '0.00%',
    formatInteger(totals.bags_without_factor),
  ]);

  autoTable(doc, {
    startY: chartEndY + 14,
    head: [['Máquina', 'Nombre', 'Bolsas buenas', 'Libras estimadas', '% del total', 'Bolsas sin factor']],
    body: rows,
    theme: 'grid',
    margin: { left: margin, right: margin, bottom: 40 },
    headStyles: { fillColor: [30, 64, 175], textColor: 255, fontStyle: 'bold', halign: 'center' },
    bodyStyles: { fontSize: 8, cellPadding: 3, halign: 'right' },
    columnStyles: {
      0: { halign: 'left', fontStyle: 'bold' },
      1: { halign: 'left' },
    },
    didParseCell: (cellData) => {
      if (cellData.section === 'body' && cellData.row.index === rows.length - 1) {
        cellData.cell.styles.fillColor = [219, 234, 254];
        cellData.cell.styles.fontStyle = 'bold';
      }
      if (cellData.section === 'body' && cellData.column.index === 5 && Number(String(cellData.cell.raw).replace(/,/g, '')) > 0) {
        cellData.cell.styles.textColor = [180, 83, 9];
        cellData.cell.styles.fontStyle = 'bold';
      }
    },
  });

  if (Number(totals.bags_without_factor || 0) > 0) {
    let noteY = (doc.lastAutoTable?.finalY || chartEndY) + 18;
    if (noteY > pageHeight - 70) {
      doc.addPage();
      noteY = margin;
    }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(180, 83, 9);
    const items = (totals.items_without_factor || []).join(', ');
    const note = `Advertencia: ${formatInteger(totals.bags_without_factor)} bolsas no se incluyen en las libras porque sus Items no tienen factor LBS${items ? `: ${items}` : '.'}`;
    doc.text(doc.splitTextToSize(note, pageWidth - (margin * 2)), margin, noteY);
  }

  const pageCount = doc.getNumberOfPages();
  for (let page = 1; page <= pageCount; page += 1) {
    doc.setPage(page);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    doc.text('Cálculo: bolsas buenas × factor LBS del Item.', margin, pageHeight - 18);
    doc.text(`Página ${page} de ${pageCount}`, pageWidth - margin, pageHeight - 18, { align: 'right' });
  }

  return doc;
};

const ConversionPoundsPdfReport = ({ onBack }) => {
  const { currentSite } = useSite();
  const previewRef = useRef(null);
  const defaultEndDate = getBusinessDateISO();
  const [startDate, setStartDate] = useState(dateDaysBefore(defaultEndDate, 6));
  const [endDate, setEndDate] = useState(defaultEndDate);
  const [includeOpen, setIncludeOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);

  useEffect(() => () => {
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
  }, [pdfUrl]);

  const clearPreview = () => {
    setReport(null);
    setPdfUrl(null);
  };

  const generatePreview = async () => {
    if (!currentSite?.id) {
      toast.error('Selecciona un sitio');
      return;
    }
    if (!startDate || !endDate || startDate > endDate) {
      toast.error('Selecciona un rango de fechas válido');
      return;
    }

    try {
      setLoading(true);
      const { data } = await axios.get('/conversion/reportes-pdf/libras', {
        params: {
          start_date: startDate,
          end_date: endDate,
          site_id: currentSite.id,
          include_open: includeOpen ? 1 : 0,
        },
      });
      const doc = buildPoundsPdf(data);
      const nextUrl = URL.createObjectURL(doc.output('blob'));
      setReport(data);
      setPdfUrl(nextUrl);
    } catch (error) {
      const detail = error.response?.data?.message
        || error.response?.data?.errors?.end_date?.[0]
        || 'No fue posible generar el reporte de libras';
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const changeFilter = (setter) => (event) => {
    setter(event.target.value);
    clearPreview();
  };

  return (
    <div className="mx-auto max-w-7xl space-y-5 px-4 py-6">
      <div>
        <button type="button" onClick={onBack} className="mb-2 text-sm font-bold text-blue-700 hover:text-blue-900">← Todos los reportes</button>
        <h1 className="text-3xl font-extrabold text-blue-900">Reporte de libras</h1>
        <p className="mt-1 text-slate-600">Libras estimadas por máquina usando las bolsas buenas y el factor LBS de cada Item.</p>
      </div>

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-end gap-4">
          <div className="min-w-[210px]">
            <label htmlFor="lbs-start-date" className="mb-1 block text-sm font-bold text-slate-700">Fecha inicial</label>
            <input id="lbs-start-date" type="date" value={startDate} onChange={changeFilter(setStartDate)} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 focus:ring-2 focus:ring-blue-500" />
          </div>
          <div className="min-w-[210px]">
            <label htmlFor="lbs-end-date" className="mb-1 block text-sm font-bold text-slate-700">Fecha final</label>
            <input id="lbs-end-date" type="date" value={endDate} onChange={changeFilter(setEndDate)} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 focus:ring-2 focus:ring-blue-500" />
          </div>
          <label className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700">
            <input
              type="checkbox"
              checked={includeOpen}
              onChange={(event) => {
                setIncludeOpen(event.target.checked);
                clearPreview();
              }}
              className="h-4 w-4"
            />
            Incluir reportes abiertos
          </label>
          <button type="button" onClick={generatePreview} disabled={loading} className="rounded-lg bg-blue-700 px-5 py-2 font-bold text-white shadow hover:bg-blue-800 disabled:cursor-not-allowed disabled:bg-slate-400">
            {loading ? 'Generando…' : 'Generar vista previa'}
          </button>
          {pdfUrl && (
            <>
              <button type="button" onClick={() => previewRef.current?.contentWindow?.print()} className="rounded-lg border border-blue-300 bg-white px-5 py-2 font-bold text-blue-800 hover:bg-blue-50">Imprimir</button>
              <a href={pdfUrl} download={`reporte-libras-${startDate}-${endDate}.pdf`} className="rounded-lg border border-slate-300 bg-white px-5 py-2 font-bold text-slate-800 no-underline hover:bg-slate-50">Descargar PDF</a>
            </>
          )}
        </div>
      </section>

      {report && (
        <>
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-xl border border-blue-100 bg-blue-50 p-4"><div className="text-xs font-bold uppercase tracking-wide text-blue-700">Máquinas</div><div className="mt-1 text-2xl font-extrabold text-blue-950">{formatInteger(report.totals.machine_count)}</div></div>
            <div className="rounded-xl border border-blue-100 bg-blue-50 p-4"><div className="text-xs font-bold uppercase tracking-wide text-blue-700">Bolsas buenas</div><div className="mt-1 text-2xl font-extrabold text-blue-950">{formatInteger(report.totals.bags_good)}</div></div>
            <div className="rounded-xl border border-amber-100 bg-amber-50 p-4"><div className="text-xs font-bold uppercase tracking-wide text-amber-700">Libras estimadas</div><div className="mt-1 text-2xl font-extrabold text-amber-950">{formatPounds(report.totals.estimated_lbs)}</div></div>
          </div>
          {Number(report.totals.bags_without_factor || 0) > 0 && (
            <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-900">
              {formatInteger(report.totals.bags_without_factor)} bolsas no se incluyeron porque sus Items no tienen factor LBS.
            </div>
          )}
        </>
      )}

      {pdfUrl ? (
        <section className="overflow-hidden rounded-2xl border border-slate-300 bg-slate-100 shadow-sm">
          <iframe ref={previewRef} src={pdfUrl} title="Vista previa del reporte de libras" className="h-[75vh] min-h-[620px] w-full" />
        </section>
      ) : (
        <section className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-16 text-center text-slate-500">
          Selecciona el periodo y genera la vista previa del reporte de libras.
        </section>
      )}
    </div>
  );
};

export default ConversionPoundsPdfReport;
